import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { PatientInfo } from 'lib/api/firebase/apis/apis';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';
import { SkippedPatient, ProgressTableRow } from '../types';
import { determinePatientEligibility } from '../utils/eligibility';
import { queryKeyFactory } from 'lib/utils/queryKeyFactory';
import { AssessmentAPI } from 'lib/api/firebase/apis/api-assessment';

interface UsePatientEligibilityProps {
  selectedPatients: PatientInfo[];
  isModalVisible: boolean;
  assessmentTypeToSend: AssessmentType | null;
}

interface UsePatientEligibilityResult {
  eligiblePatients: PatientInfo[];
  skippedPatients: SkippedPatient[];
  confirmationRows: ProgressTableRow[];
  isQueriesLoading: boolean;
  analyzedCount: number;
  remainingCount: number;
}

export function usePatientEligibility({
  selectedPatients,
  isModalVisible,
  assessmentTypeToSend
}: UsePatientEligibilityProps): UsePatientEligibilityResult {
  const assessmentQueries = useQueries({
    queries: selectedPatients.map(patient => ({
      queryKey: queryKeyFactory.assessment.list(patient.id),
      queryFn: () => AssessmentAPI.list({ patientId: patient.id }),
      enabled: selectedPatients.length > 0 && isModalVisible
    }))
  });

  const isQueriesLoading = assessmentQueries.some(q => q.isLoading);
  const analyzedCount = assessmentQueries.filter(q => !q.isLoading).length;
  const remainingCount = selectedPatients.length - analyzedCount;

  const { eligiblePatients, skippedPatients, confirmationRows } = useMemo(() => {
    if (!isModalVisible || isQueriesLoading) {
      return { eligiblePatients: [], skippedPatients: [], confirmationRows: [] };
    }

    const eligible: PatientInfo[] = [];
    const skipped: SkippedPatient[] = [];

    const rows = selectedPatients.map((patient, index) => {
      const queryResult = assessmentQueries[index];
      const assessments = queryResult?.data?.data ?? [];

      const eligibility = determinePatientEligibility(assessments, assessmentTypeToSend);

      if (eligibility.phase_1_skipped) {
        skipped.push({
          ...patient,
          activeStatus: eligibility.activeStatus || undefined,
          activeType: assessments.find(a => a.status === 'pending' || a.status === 'started')?.assessmentType
        });
      } else {
        eligible.push(patient);
      }

      return {
        id: patient.id,
        displayName: patient.displayName,
        email: patient.email,
        activeStatus: eligibility.activeStatus,
        isSkipped: eligibility.phase_1_skipped,
        phase_1: eligibility.phase_1_skipped ? (false as const) : null,
        phase_1_error: null,
        phase_2: eligibility.phase_2_skipped ? (false as const) : null,
        phase_2_error: null,
        rawPatient: patient
      };
    });

    return { eligiblePatients: eligible, skippedPatients: skipped, confirmationRows: rows };
  }, [selectedPatients, assessmentQueries, isQueriesLoading, isModalVisible, assessmentTypeToSend]);

  return {
    eligiblePatients,
    skippedPatients,
    confirmationRows,
    isQueriesLoading,
    analyzedCount,
    remainingCount
  };
}
